import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Utilities } from '../class/utilities';
import { BranchActivityRange, IOperationNcmRange, ITableCstCsosn, Operation } from '../interface/operation';
import { OperationFunction } from '../shared/lists/operation-functions';
import { firstValueFrom } from 'rxjs';
import { DataService } from './data.service';
import { IbsCbsTax } from '@app/interface/fiscal/ibs-cbs-tax';
import { IsTax } from '@app/interface/fiscal/is-tax';

interface ApiOperation {
  _id?: string;
  description?: string;
  inOut?: boolean;
  available?: boolean;
  DRE: boolean;
  titleType?: string[]; // required
  function?: OperationFunction;
  descriptionFunction?: string;
  financialGenera?: boolean;
  docFiscal?: boolean;
  docFiscalServico?: boolean;
  moveStock?: boolean;
  moveMediumCost?: boolean;
  sumIpiInPartCost?: boolean;
  opDefault?: boolean;
  priceReadjusts?: {
    available: boolean;
    mediumCost: boolean; // !lastCost
    lastCost: boolean; // !mediumCOst
  };
  ICMS?: {
    CST?: string;
    Message?: string;
    Aliquot?: number;
    CFOP?: string;
    CFOP_I?: string;
  };
  PIS?: {
    CST?: string;
    Message?: string;
    Aliquot?: number;
  };
  COFINS?: {
    CST?: string;
    Message?: string;
    Aliquot?: number;
  };
  IPI?: string;
  superSimples?: number;
  Message?: string;
  opercionEstadual: {
    naturalezaSubsTributariaCST10: string;
    naturalezaSubsTributariaCST60: string;
    naturalezaSTConsumidorCST10: string;
    naturalezaSTConsumidorCST60: string;
    naturalezaNFServicios: string;
    naturalezaCombLubrificante: string;
    naturalezaItemIndustrializado: string;
    naturalezaNaoContribuinte: string;
  };
  opercionInterestadual: {
    naturalezaSubsTributariaCST10: string;
    naturalezaSubsTributariaCST60: string;
    naturalezaSTConsumidorCST10: string;
    naturalezaSTConsumidorCST60: string;
    naturalezaNFServicios: string;
    naturalezaCombLubrificante: string;
    naturalezaItemIndustrializado: string;
    naturalezaNaoContribuinte: string;
  };

  expenseType?: string;
  createdAt?: string;
  updatedAt?: string;


  IRPJ?: {
    aliquotPart?: number;
    aliquotLabor?: number;
    valMinRet?: number;
    msg?: string;
  };

  INSS?: {
    aliquot?: number;
    valMinRet?: number;
    msg?: string;
  };

  CSLL?: {
    aliquot?: number;
    valMinRet?: number;
    msg?: string;
  };  

  IBS_CBS?: IbsCbsTax;
  laborsIBS_CBS?: IbsCbsTax;
  IS?: IsTax;

  ncmRange?: IOperationNcmRange[];
  branchActivityRange?: BranchActivityRange[];

  nfseIndFinal?: 0 | 1;
  nfseIndOperation?: string;
  nfseTpOper?: Operation["nfseTpOper"];
  nbsCode?: string;

  tableCstCsosn?: ITableCstCsosn[]; 
}

const BRANCH_ACTIVITY_TAX_GROUPS = ['IS', 'IBS_CBS', 'laborsIBS_CBS'] as const;

@Injectable({
  providedIn: 'root'
})
export class OperationService {

  private _isFromMatrix = true;

  /** @todo set as Observable */
  operations: Operation[] = [];

  constructor(
    private http: HttpClient,
    private dataService: DataService,
  ) { }

  // private async setOperations(){
  //   this.operations = await this.getAll();
  // }

  async getAll(cnpj?: string): Promise<Operation[]> {
    const url = `${environment.mkgoURL}/api/v1/operation`;
    const header = await firstValueFrom(this.dataService.httpOptions(cnpj || this._isFromMatrix))
    const resp: any = await this.http.get(url, header).pipe(first()).toPromise()
    let operations = (resp["operations"] as ApiOperation[]).reverse();
    this.operations = operations.map(this.complyAPP);
    return this.operations;
  }

  async getById(id: string): Promise<Operation> {
    const url = `${environment.mkgoURL}/api/v1/operation/${id}`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))
    const resp: any = await this.http.get(url, header).pipe(first()).toPromise();
    return this.complyAPP(resp);
  }

  async getByIds(ids: string[]): Promise<Operation[]> {
    const url = `${environment.mkgoURL}/api/v1/operation/by-ids`;
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))
    const resp: any = await this.http.post(url, { ids }, header).pipe(first()).toPromise();
    return (resp as ApiOperation[]).map(op => this.complyAPP(op));
  }

  async register(operation: Operation): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/operation`;
    const apiOperation = this.complyAPI(operation);
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: any = await this.http.post(url, apiOperation, header).pipe(first()).toPromise();
    return resp['id'];
  }

  async update(operation: Operation): Promise<string> {
    const url = `${environment.mkgoURL}/api/v1/operation/${operation.id}`;
    const apiOperation = this.complyAPI(operation);
    const header = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))
    const resp: any = await this.http.put(url, apiOperation, header).pipe(first()).toPromise();
    return resp['id'];
  }

  async changeStatus(operationId: string, status: 0 | 1) {
    const url = `${environment.mkgoURL}/api/v1/operation/${operationId}/status/${status}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: any = await this.http.post(url, null, options).pipe(first()).toPromise();
    return resp;
  }

  private complyAPI(operation: Operation): ApiOperation {
    let apiObject: ApiOperation = {
      COFINS: operation.COFINS,
      ICMS: operation.ICMS,
      IPI: operation.IPI,
      PIS: operation.PIS,
      CSLL: operation.CSLL,
      INSS: operation.INSS,
      IRPJ: operation.IRPJ,
      DRE: operation.DRE,
      available: operation.available,
      description: operation.description,
      expenseType: operation.expenseType ? operation.expenseType._id : undefined,
      financialGenera: operation.generateFinancial,
      docFiscal: operation.docFiscal,
      docFiscalServico: operation.docFiscalServico,
      function: operation.operationFunction,
      descriptionFunction: operation.descriptionFunction,
      inOut: operation.entry,
      Message: operation.operationMessage,
      moveStock: operation.changeStock,
      moveMediumCost: operation.changeMidleCost,
      sumIpiInPartCost: operation.sumIpiInPartCost,
      opercionEstadual: {
        naturalezaCombLubrificante: operation.stateOperations.naturalezaCombLubrificante,
        naturalezaItemIndustrializado: operation.stateOperations.naturalezaItemIndustrializado,
        naturalezaNFServicios: operation.stateOperations.naturalezaNFServicios,
        naturalezaNaoContribuinte: operation.stateOperations.naturalezaNaoContribuinte,
        naturalezaSTConsumidorCST10: operation.stateOperations.naturalezaSTConsumidorCST10,
        naturalezaSTConsumidorCST60: operation.stateOperations.naturalezaSTConsumidorCST60,
        naturalezaSubsTributariaCST10: operation.stateOperations.naturalezaSubsTributariaCST10,
        naturalezaSubsTributariaCST60: operation.stateOperations.naturalezaSubsTributariaCST60
      },
      opercionInterestadual: {
        naturalezaCombLubrificante: operation.interstateOperations.naturalezaCombLubrificante,
        naturalezaItemIndustrializado: operation.interstateOperations.naturalezaItemIndustrializado,
        naturalezaNFServicios: operation.interstateOperations.naturalezaNFServicios,
        naturalezaNaoContribuinte: operation.interstateOperations.naturalezaNaoContribuinte,
        naturalezaSTConsumidorCST10: operation.interstateOperations.naturalezaSTConsumidorCST10,
        naturalezaSTConsumidorCST60: operation.interstateOperations.naturalezaSTConsumidorCST60,
        naturalezaSubsTributariaCST10: operation.interstateOperations.naturalezaSubsTributariaCST10,
        naturalezaSubsTributariaCST60: operation.interstateOperations.naturalezaSubsTributariaCST60
      },
      priceReadjusts: {
        available: Boolean(operation.readjustPriceAtEntry),
        mediumCost: operation.readjustBy == "averageCost",
        lastCost: operation.readjustBy == "lastCost",
      },
      opDefault: operation.opDefault,
      superSimples: operation.superSimples,
      titleType: [operation.titleType?._id || operation.titleTypeId],
      IS: operation.IS,
      IBS_CBS: operation.IBS_CBS,
      laborsIBS_CBS: operation.laborsIBS_CBS,
      branchActivityRange: this.formatBranchActivityItems(operation.branchActivityRange) ?? [],
      ncmRange: this.formatNcmRangeItems(operation.ncmRange) ?? [],
      nfseIndFinal: operation.nfseIndFinal,
      nfseIndOperation: operation.nfseIndOperation,
      nfseTpOper: operation.nfseTpOper,
      nbsCode: operation.nbsCode,
      tableCstCsosn: operation.tableCstCsosn ?? [],
    }

    apiObject = Utilities.removeNull(apiObject)

    if (!("nfseTpOper" in apiObject)) apiObject.nfseTpOper = null;

    return apiObject
  }

  private complyAPP(apiObject: ApiOperation): Operation {
    if (!apiObject._id) {
      console.error(`Api object sem id ${apiObject}`)
      if ((apiObject as any).length) {
        console.error(`Api object veio como um array ${(apiObject as any).length}`)
      }
    }
    
    let operation: Operation = {
      updatedAt: apiObject.updatedAt,
      id: apiObject._id,
      COFINS: apiObject.COFINS,
      ICMS: apiObject.ICMS,
      PIS: apiObject.PIS,
      IPI: apiObject.IPI,
      CSLL: apiObject.CSLL,
      INSS: apiObject.INSS,
      IRPJ: apiObject.IRPJ,
      DRE: apiObject.DRE,
      available: apiObject.available,
      changeMidleCost: apiObject.moveMediumCost,
      sumIpiInPartCost: apiObject.sumIpiInPartCost,
      changeStock: apiObject.moveStock,
      description: apiObject.description,
      entry: apiObject.inOut,
      generateFinancial: apiObject.financialGenera,
      opDefault: apiObject.opDefault, 
      docFiscal: apiObject.docFiscal,
      docFiscalServico: apiObject.docFiscalServico,
      interstateOperations: apiObject.opercionInterestadual,
      operationFunction: apiObject.function,
      descriptionFunction: apiObject.descriptionFunction,
      readjustPriceAtEntry: apiObject.priceReadjusts?.available,
      readjustBy: apiObject.priceReadjusts?.mediumCost ? "averageCost" : apiObject.priceReadjusts?.lastCost ? "lastCost" : undefined,
      operationMessage: apiObject.Message,
      stateOperations: apiObject.opercionEstadual,
      superSimples: apiObject.superSimples,
      expenseTypeId: apiObject.expenseType,
      titleTypeId: apiObject.titleType?.[0],
      ncmRange: apiObject.ncmRange ?? [],
      branchActivityRange: apiObject.branchActivityRange,
      IBS_CBS: apiObject.IBS_CBS,
      laborsIBS_CBS: apiObject.laborsIBS_CBS,
      IS: apiObject.IS,
      nfseIndFinal: apiObject.nfseIndFinal,
      nfseIndOperation: apiObject.nfseIndOperation,
      nfseTpOper: apiObject.nfseTpOper,
      nbsCode: apiObject.nbsCode,
      tableCstCsosn: apiObject.tableCstCsosn ?? [],
    };
    return operation;
  }

  /**
   * Same contract as `formatNcmRangeItems` for a checked group — the `calc*` flag is consumed
   * and the group travels with its values — but an unchecked group is blanked instead of
   * deleted. Deleting it leaves the path out of the PUT body, so the API's `$set` never
   * touches it and the CST the operator just cleared stays persisted.
   *
   * Kept separate from `formatNcmRangeItems` on purpose: `ncmRange` shares that function with
   * every other tax group, and switching it to blanking would change what persists on every
   * operation save.
   */
  formatBranchActivityItems(branchActivityRanges: BranchActivityRange[]): BranchActivityRange[] {
    return (branchActivityRanges ?? []).map(branchActivityRange => {
      for (const groupKey of BRANCH_ACTIVITY_TAX_GROUPS) {
        const group = branchActivityRange[groupKey];

        if (!group) continue;

        const flagKey = `calc${groupKey}`;

        if (group[flagKey]) {
          delete group[flagKey];
          branchActivityRange[groupKey] = Utilities.removeNull(group);
        } else {
          branchActivityRange[groupKey] = <IbsCbsTax & IsTax>{ CST: '', cClassTrib: '' };
        }
      }

      return branchActivityRange;
    });
  }

  formatNcmRangeItems(ncmRanges: IOperationNcmRange[] | BranchActivityRange[]) {
    return ncmRanges.map(ncmRange => {
      const keys = Object.keys(ncmRange);

      for (const key of keys) {
        switch (key) {
          case "IPI":
            if (!ncmRange[key]?.["calc" + key]) {
              delete ncmRange[key];
            } else {
              delete ncmRange[key]?.["calc" + key];
              ncmRange[key] = ncmRange[key][key];
            }
            break;
          case "superSimples":
            if (!ncmRange[key]?.["calc" + key]) {
              delete ncmRange[key];
            } else {
              delete ncmRange[key]?.["calc" + key];
              ncmRange[key] = ncmRange[key][key];
            }
            break;
          case "IS":
          case "IBS_CBS":
          case "laborsIBS_CBS":
          case "COFINS":
          case "PIS":
          case "ICMS":
          case "ICMS_ST":
          case "IRPJ":
          case "INSS":
          case "CSLL":
            if (!ncmRange[key]?.["calc" + key]) {
              delete ncmRange[key];
            } else {
              delete ncmRange[key]?.["calc" + key];
              ncmRange[key] = Utilities.removeNull(ncmRange[key])
            }
            break;
          default: 
        }
      }
      
      return ncmRange;
    })
  }
}


